<script setup lang="ts">
import { computed } from "vue";
import { bss } from "@dashboard/base/BssService";
import { formatCopyrightYears } from "@dashboard/utils/formatCopyrightYears";
import { getBssValue } from "@dashboard/utils/getBssValue";
import { RC_PARTNER_NAME as partnerName } from "@dashboard/brands/brandConfig";
import type { BrandBssKeys, BrandFooterConfig, FooterLink } from "@dashboard/brands/brandConfig";

interface Props {
  copyrightYearFrom?: string;
  copyrightExtraText?: string;
  links?: FooterLink[];
  footer?: BrandFooterConfig;
  bssKeys?: BrandBssKeys;
}

defineOptions({ inheritAttrs: false });
const props = withDefaults(defineProps<Props>(), {
  copyrightYearFrom: "",
  copyrightExtraText: "",
  links: () => [],
  footer: () => ({ layout: "standard", copyClass: "footer-copy", menuClass: "footer-menu" }),
});

function copyrightValue(): string {
  return "\u00A9 " + formatCopyrightYears(props.copyrightYearFrom) + " " + props.copyrightExtraText;
}

const brandName = computed(() => getBssValue(props.bssKeys?.brandName));
const copyrightText = computed(() => getBssValue(props.bssKeys?.copyright));
const termsLink = computed(() => props.links.find((link) => link.className === "policy"));
const tosLink = computed(() => {
  const fallback = termsLink.value?.href ?? "";
  const binding = props.bssKeys?.tosLink;
  return binding ? bss.get(binding.key, fallback) : fallback;
});
const partnersLink = computed(() => getBssValue(props.bssKeys?.partnersLink));
const partnersLogoUrl = computed(() => getBssValue(props.bssKeys?.partnersLogoUrl));
const hasPartnersLogo = computed(() => {
  const binding = props.bssKeys?.partnersLogoUrl;
  return binding ? bss.has(binding.key) : false;
});
</script>

<template>
  <footer class="rc-layout">
    <div class="rc-layout-inner">
      <template v-if="props.footer.layout === 'att'">
        <div>
          <p>
            By subscribing to and/or using {{ brandName }}, you acknowledge agreement to AT&amp;T's
            <a
              v-if="termsLink"
              class="policy CId-link-policy IId-link-policy"
              :href="tosLink"
              :title="`AT&T's ${termsLink.label}`"
              target="_blank"
              rel="noopener"
            >{{ termsLink.label }}</a>.
          </p>
          <p id="copyright">
            {{ copyrightValue() }} {{ copyrightText }}<br>
            AT&amp;T and the AT&amp;T logo are trademarks of AT&amp;T Intellectual Property.
          </p>
        </div>
        <div>
          <a
            :href="partnersLink"
            class="rc-partners-logo CId-link-partners-logo IId-link-partners-logo"
            :class="{ 'has-bss-partners-logo': hasPartnersLogo }"
            :title="partnerName"
            target="_blank"
            rel="noopener"
          >
            <img v-if="hasPartnersLogo" :src="partnersLogoUrl" :alt="partnerName">
          </a>
        </div>
      </template>
      <p v-else-if="props.footer.layout === 'copyright'" id="copyright">{{ copyrightValue() }}</p>
      <template v-else>
        <div v-bind="props.footer.copyClass ? { class: props.footer.copyClass } : {}">
          <ul v-if="props.links.length" :class="props.footer.menuClass">
            <li v-for="(link, index) in props.links" :key="index">
              <a
                :class="link.className ? [link.className, `CId-link-${link.className}`, `IId-link-${link.className}`] : undefined"
                :href="link.href"
                target="_blank"
                rel="noopener"
              >{{ link.label }}</a>
            </li>
          </ul>
          <p :id="props.footer.copyrightId">
            {{ copyrightValue() }}{{ props.footer.copyrightSuffix }}
            <template v-for="(line, index) in props.footer.noticeLines" :key="index"><br>{{ line }}</template>
            <br v-if="props.footer.trailingBreak">
          </p>
        </div>
        <div v-if="props.footer.showRingCentralLogo">
          <div class="logo-rc" />
        </div>
      </template>
    </div>
  </footer>
</template>
